// client/src/services/ChatService.js
import { io } from 'socket.io-client'
import { isMobilePlatform, getMobileAuthToken } from '../utils/mobileAuth.js'
import { getApiBaseUrl } from '../api/FlexFetch.js'

class ChatService {
  constructor() {
    this.socket = null
    this.isConnected = false
    this.messageHandlers = []
    this.errorHandlers = []
    this.typingHandlers = []
    this.presenceHandlers = []
    this.typingTimeouts = new Map() // Track typing timeouts per channel
    this.connectPromise = null // Track pending connection attempts
    this.connectTimeout = null // Track connection timeout for cleanup
    this.reconnectAttempts = 0 // Track reconnection attempts for backoff
    this.maxReconnectDelay = 30000 // Maximum backoff delay (30 seconds)
    this.baseReconnectDelay = 1000 // Base delay (1 second)
  }

  /**
   * Calculate exponential backoff delay
   * Returns delay in milliseconds with jitter to prevent thundering herd
   */
  getReconnectDelay() {
    // Exponential backoff: baseDelay * 2^attempts
    const exponentialDelay =
      this.baseReconnectDelay * Math.pow(2, this.reconnectAttempts)

    // Cap at max delay
    const cappedDelay = Math.min(exponentialDelay, this.maxReconnectDelay)

    // Add jitter (±25%) to prevent synchronized reconnection attempts
    const jitter = cappedDelay * 0.25 * (Math.random() - 0.5)

    return Math.floor(cappedDelay + jitter)
  }

  connect() {
    // Already connected
    if (this.socket && this.socket.connected) {
      this.reconnectAttempts = 0 // Reset on successful connection
      return Promise.resolve()
    }

    // Connection attempt already in progress - return the existing promise
    if (this.connectPromise) {
      return this.connectPromise
    }

    this.connectPromise = new Promise((resolve, reject) => {
      // Use the same API base URL logic as HTTP requests to ensure consistency
      // This is especially important for Capacitor/mobile where window.location is not the API server
      const serverUrl = getApiBaseUrl()

      // Clean up any existing socket before creating a new one
      if (this.socket) {
        this.socket.removeAllListeners()
        this.socket.disconnect()
        this.socket = null
      }

      // Build socket.io options
      const socketOptions = {
        withCredentials: true,
        timeout: 10000,
        transports: ['websocket', 'polling'],
      }

      // If mobile platform, add auth token to handshake
      if (isMobilePlatform()) {
        const token = getMobileAuthToken()
        if (token) {
          socketOptions.auth = { token }
        }
      }

      this.socket = io(serverUrl, socketOptions)

      // Clear any existing timeout
      if (this.connectTimeout) {
        clearTimeout(this.connectTimeout)
      }

      this.connectTimeout = setTimeout(() => {
        this.connectTimeout = null
        this.connectPromise = null
        reject(
          new Error(
            'Connection timeout - please check your internet connection',
          ),
        )
      }, 15000)

      this.socket.on('connect', () => {
        console.log('Connected to chat server')
        this.isConnected = true
        this.reconnectAttempts = 0 // Reset backoff on successful connection
        this.connectPromise = null
        if (this.connectTimeout) {
          clearTimeout(this.connectTimeout)
          this.connectTimeout = null
        }
        resolve()
      })

      this.socket.on('connect_error', (error) => {
        console.error('Chat connection error:', error)
        this.isConnected = false
        this.reconnectAttempts++ // Increment for backoff calculation
        this.connectPromise = null
        if (this.connectTimeout) {
          clearTimeout(this.connectTimeout)
          this.connectTimeout = null
        }

        // Calculate backoff delay
        const delay = this.getReconnectDelay()
        console.log(
          `Chat reconnection attempt ${this.reconnectAttempts} failed. Retrying in ${delay}ms...`,
        )

        // Provide user-friendly error messages
        let userMessage = 'Failed to connect to chat service'
        if (error.message.includes('Authentication')) {
          userMessage =
            'Authentication failed - please refresh and log in again'
        } else if (error.message.includes('timeout')) {
          userMessage =
            'Connection timeout - please check your internet connection'
        } else if (error.type === 'TransportError') {
          userMessage =
            'Network error - please check your connection and try again'
        }

        reject(new Error(userMessage))
      })

      this.socket.on('disconnect', () => {
        console.log('Disconnected from chat server')
        this.isConnected = false
        // Notify error handlers about disconnection
        this.errorHandlers.forEach((handler) =>
          handler(new Error('Chat connection lost')),
        )
      })

      this.socket.on('new_message', (message) => {
        this.messageHandlers.forEach((handler) => handler(message))
      })

      this.socket.on('message_edited', (message) => {
        this.messageHandlers.forEach((handler) => handler(message, 'edit'))
      })

      this.socket.on('message_deleted', ({ messageId }) => {
        this.messageHandlers.forEach((handler) =>
          handler({ id: messageId }, 'delete'),
        )
      })

      this.socket.on('user_typing', (data) => {
        this.typingHandlers.forEach((handler) => handler(data))
      })

      this.socket.on('user_presence', (data) => {
        this.presenceHandlers.forEach((handler) => handler(data))
      })

      this.socket.on('chat_error', (error) => {
        console.error('Chat error:', error)
        const userMessage =
          error.message || 'An error occurred with the chat service'
        this.errorHandlers.forEach((handler) => handler(new Error(userMessage)))
      })
    })

    return this.connectPromise
  }

  disconnect() {
    // Clear any pending connection timeout
    if (this.connectTimeout) {
      clearTimeout(this.connectTimeout)
      this.connectTimeout = null
    }
    this.connectPromise = null
    this.reconnectAttempts = 0 // Reset backoff on manual disconnect

    // Clear all typing timeouts to prevent memory leaks
    this.typingTimeouts.forEach((timeout) => clearTimeout(timeout))
    this.typingTimeouts.clear()

    if (this.socket) {
      this.socket.removeAllListeners()
      this.socket.disconnect()
      this.socket = null
      this.isConnected = false
    }
  }

  sendMessage(channelId, body) {
    if (!this.socket || !this.isConnected) {
      throw new Error('Not connected to chat server')
    }

    return new Promise((resolve, reject) => {
      let timeout

      const cleanup = () => {
        if (timeout) clearTimeout(timeout)
        if (this.socket) {
          this.socket.off('new_message', messageHandler)
          this.socket.off('chat_error', errorHandler)
        }
      }

      const messageHandler = (message) => {
        if (message.channel_id === channelId) {
          cleanup()
          resolve(message)
        }
      }

      const errorHandler = (error) => {
        cleanup()
        reject(new Error(error.message))
      }

      timeout = setTimeout(() => {
        cleanup()
        reject(new Error('Message send timeout'))
      }, 5000)

      this.socket.on('new_message', messageHandler)
      this.socket.on('chat_error', errorHandler)

      this.socket.emit('send_message', { channelId, body })
    })
  }

  joinChannel(channelId) {
    if (!this.socket || !this.isConnected) {
      throw new Error('Not connected to chat server')
    }

    this.socket.emit('join_channel', { channelId })
  }

  editMessage(messageId, body) {
    if (!this.socket || !this.isConnected) {
      throw new Error('Not connected to chat server')
    }

    return new Promise((resolve, reject) => {
      let timeout

      const cleanup = () => {
        if (timeout) clearTimeout(timeout)
        if (this.socket) {
          this.socket.off('message_edited', messageHandler)
          this.socket.off('chat_error', errorHandler)
        }
      }

      const messageHandler = (message) => {
        if (message.id === messageId) {
          cleanup()
          resolve(message)
        }
      }

      const errorHandler = (error) => {
        cleanup()
        reject(new Error(error.message))
      }

      timeout = setTimeout(() => {
        cleanup()
        reject(new Error('Message edit timeout'))
      }, 5000)

      this.socket.on('message_edited', messageHandler)
      this.socket.on('chat_error', errorHandler)

      this.socket.emit('edit_message', { messageId, body })
    })
  }

  deleteMessage(messageId) {
    if (!this.socket || !this.isConnected) {
      throw new Error('Not connected to chat server')
    }

    return new Promise((resolve, reject) => {
      let timeout

      const cleanup = () => {
        if (timeout) clearTimeout(timeout)
        if (this.socket) {
          this.socket.off('message_deleted', messageHandler)
          this.socket.off('chat_error', errorHandler)
        }
      }

      const messageHandler = (data) => {
        if (data.messageId === messageId) {
          cleanup()
          resolve(data)
        }
      }

      const errorHandler = (error) => {
        cleanup()
        reject(new Error(error.message))
      }

      timeout = setTimeout(() => {
        cleanup()
        reject(new Error('Message delete timeout'))
      }, 5000)

      this.socket.on('message_deleted', messageHandler)
      this.socket.on('chat_error', errorHandler)

      this.socket.emit('delete_message', { messageId })
    })
  }

  startTyping(channelId) {
    if (!this.socket || !this.isConnected) return

    // Clear any existing timeout for this channel
    if (this.typingTimeouts.has(channelId)) {
      clearTimeout(this.typingTimeouts.get(channelId))
    }

    // Send typing start event
    this.socket.emit('typing_start', { channelId })

    // Auto-stop typing after 3 seconds of inactivity
    const timeout = setTimeout(() => {
      this.stopTyping(channelId)
    }, 3000)

    this.typingTimeouts.set(channelId, timeout)
  }

  stopTyping(channelId) {
    if (!this.socket || !this.isConnected) return

    // Clear timeout
    if (this.typingTimeouts.has(channelId)) {
      clearTimeout(this.typingTimeouts.get(channelId))
      this.typingTimeouts.delete(channelId)
    }

    // Send typing stop event
    this.socket.emit('typing_stop', { channelId })
  }

  onTyping(handler) {
    this.typingHandlers.push(handler)

    // Return unsubscribe function
    return () => {
      const index = this.typingHandlers.indexOf(handler)
      if (index > -1) {
        this.typingHandlers.splice(index, 1)
      }
    }
  }

  onPresence(handler) {
    this.presenceHandlers.push(handler)

    // Return unsubscribe function
    return () => {
      const index = this.presenceHandlers.indexOf(handler)
      if (index > -1) {
        this.presenceHandlers.splice(index, 1)
      }
    }
  }

  onMessage(handler) {
    this.messageHandlers.push(handler)

    // Return unsubscribe function
    return () => {
      const index = this.messageHandlers.indexOf(handler)
      if (index > -1) {
        this.messageHandlers.splice(index, 1)
      }
    }
  }

  onError(handler) {
    this.errorHandlers.push(handler)

    // Return unsubscribe function
    return () => {
      const index = this.errorHandlers.indexOf(handler)
      if (index > -1) {
        this.errorHandlers.splice(index, 1)
      }
    }
  }
}

// Export singleton instance
const chatService = new ChatService()
export default chatService
